import React from 'react';
import {cn} from '@chekin/guestapp-ui-kit';
import {FieldError} from 'react-hook-form';
import {SelectOption} from '@chekin/core';
import {RadioInput, RadioLabel, RadioOption, RadioWrapper} from './styled';

type RadioOptionType<V = string> = SelectOption<unknown, V>;

export type MultiRadioProps = {
  onChange: (opt: RadioOptionType) => void;
  options: RadioOptionType[];
  value?: RadioOptionType;
  defaultValue?: string | number;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  error?: FieldError;
  name?: string;
  fieldSetId?: string;
  isCustomBox?: boolean;
  selectedOptionIcon?: React.ReactNode;
};

const MultiRadio = React.forwardRef<HTMLDivElement, MultiRadioProps>(
  (
    {
      value,
      onChange,
      options,
      disabled,
      multiple,
      className,
      defaultValue,
      fieldSetId,
      isCustomBox,
      selectedOptionIcon,
    },
    ref,
  ) => {
    React.useEffect(
      function setDefaultValue() {
        const option = options.find(opt => opt.value === defaultValue);
        if (defaultValue && option && !value) {
          onChange(option);
        }
      },
      [defaultValue, onChange, options, value],
    );
    const RadioOptions = React.useMemo(() => {
      return options.map(opt => {
        const isChecked = opt.value === value?.value;

        const classes = cn(
          className,
          'multi-radio__option',
          'kiosko:p-[14px] kiosko:rounded-xl',
          {
            'multi-radio-option_checked': isChecked,
          },
        );
        const handleChange = () => {
          if (!disabled) onChange(opt);
        };

        return (
          <RadioOption
            className={classes}
            key={opt.value}
            $isCustomBox={isCustomBox}
            $isChecked={isChecked}
            $hasSelectedIcon={isChecked && !!selectedOptionIcon}
          >
            <div>
              <RadioInput
                multiple={multiple}
                type="radio"
                id={opt.value}
                name={fieldSetId ? fieldSetId : 'drone'}
                value={opt.value}
                onChange={handleChange}
                checked={isChecked}
              />
              <RadioLabel
                htmlFor={opt.value}
                className={cn(
                  'multi-radio__label',
                  'before:kiosko:!w-12 before:kiosko:!h-12 before:kiosko:!mr-[38px]',
                  'kiosko:!font-normal kiosko:!leading-[56px] kiosko:!text-kiosko-m',
                )}
              >
                {opt.label}
              </RadioLabel>
            </div>
            {isChecked && selectedOptionIcon}
          </RadioOption>
        );
      });
    }, [
      options,
      value?.value,
      multiple,
      disabled,
      onChange,
      fieldSetId,
      isCustomBox,
      className,
      selectedOptionIcon,
    ]);

    return (
      <RadioWrapper
        ref={ref}
        className={cn(className, 'multi-radio', 'kiosko:gap-[56px]')}
      >
        {RadioOptions}
      </RadioWrapper>
    );
  },
);

export {MultiRadio};
export type {RadioOptionType};
